import { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Table } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { getCompanyInfo } from '../../../../api/company';
import { useGetCompany } from '../../../../hooks/useCompany';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { handleDownloadExcel } from '../../../../libs/utils';
import SearchFilter from './SearchFilter';

const columns = [
  {
    title: 'รหัสบริษัท',
    dataIndex: 'code',
    key: 'code',
    width: 120,
    sorter: true,
    defaultSortOrder: 'ascend',
    align: 'center',
    render: (value) => value || '-',
  },
  {
    title: 'ชื่อบริษัท',
    dataIndex: 'company_name',
    key: 'company_name',
    width: 200,
    sorter: true,
    align: 'center',
    render: (value) => value || '-',
  },
  {
    title: 'ชื่อผู้มีอำนาจ',
    dataIndex: 'people_name',
    key: 'people_name',
    width: 200,
    sorter: true,
    align: 'center',
    render: (value) => value || '-',
  },
  {
    title: 'คะแนนสะสม',
    dataIndex: 'accumulated_points',
    key: 'accumulated_points',
    width: 120,
    sorter: true,
    align: 'center',
    render: (value) => (value ? value.toFixed(2) : '0'),
  },
  {
    title: 'เกณฑ์การให้คะแนน',
    dataIndex: 'spending_criteria',
    key: 'spending_criteria',
    width: 160,
    sorter: true,
    align: 'center',
    render: (value) => (value ? value.toFixed(2) : '0'),
  },
  {
    title: 'ยอดที่ใช้ไปล่าสุด',
    dataIndex: 'spending_amount',
    key: 'spending_amount',
    width: 150,
    sorter: true,
    align: 'center',
    render: (value) => (value ? value.toFixed(2) : '0'),
  },
  {
    title: 'วันที่สมัคร',
    dataIndex: 'register_timestamp',
    key: 'register_timestamp',
    width: 120,
    sorter: true,
    align: 'center',
    render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
  },
];

const CompanyList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const debouncedSearch = useDebounceValue(search, 500);

  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
    sort_col: 'code',
    sort_value: true,
    search: undefined,
  });

  const { data: companyData, isLoading: companyLoading } = useGetCompany(query);

  const handleRowClicked = (row) => {
    navigate(`/admin/${row._id}`);
  };

  const handleSearch = useCallback((e) => {
    setQuery((prev) => ({
      ...prev,
      page: 1,
    }));

    setSearch(e.target.value);
  }, []);

  const handleExport = useCallback(async () => {
    try {
      setIsExporting(true);

      const response = await getCompanyInfo({
        page: 1,
        limit: 'all',
        sort_col: 'code',
        sort_value: true,
      });

      if (response.Code === '20000') {
        const formatData = response.Data.data.map((item) => ({
          code: item.code,
          company_name: item.company_name,
          people_name: item.people_name,
          spending_amount: item.spending_amount,
          spending_criteria: item.spending_criteria,
          accumulated_points: item.accumulated_points,
          create_timestamp: item.create_timestamp,
          update_timestamp: item.update_timestamp,
          register_timestamp: item.register_timestamp,
          remove_status: item.remove_status,
          state: item.state,
        }));

        handleDownloadExcel(
          formatData,
          'Company list',
          `STARCARD_CLUB_COMPANY_${dayjs().format('DD-MMM-YYYY')}`,
          {
            header: [
              'code',
              'company_name',
              'people_name',
              'spending_amount',
              'spending_criteria',
              'accumulated_points',
              'create_timestamp',
              'update_timestamp',
              'register_timestamp',
              'remove_status',
              'state',
            ],
          }
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsExporting(false);
    }
  }, []);

  useEffect(() => {
    setQuery((prev) => ({ ...prev, page: 1, search: debouncedSearch || undefined }));
  }, [debouncedSearch]);

  return (
    <Box>
      <Box
        sx={{
          padding: '16px',
          backgroundColor: 'white',
          borderBottomLeftRadius: '15px',
          borderBottomRightRadius: '15px',
          border: '2px solid white',
          boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
          '& .ant-pagination': {
            alignItems: 'center',
          },
          '& .ant-pagination-options': {
            alignSelf: 'center',
          },
        }}
      >
        <SearchFilter
          search={search}
          handleSearch={handleSearch}
          handleExport={handleExport}
          isExporting={isExporting}
        />

        <Table
          bordered
          rowKey="_id"
          columns={columns}
          dataSource={companyData?.Data.data || []}
          loading={companyLoading}
          pagination={{
            current: query.page,
            pageSize: query.limit,
            total: companyData?.Data.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '50', '100', '500'],
            hideOnSinglePage: true,
          }}
          onChange={(pagination, filters, sorter) => {
            setQuery((prev) => ({
              ...prev,
              page: pagination.current,
              limit: pagination.pageSize,
              sort_col: sorter.field || 'code',
              sort_value: sorter.order === 'ascend',
            }));
          }}
          onRow={(record) => {
            return {
              onClick: () => handleRowClicked(record),
              style: { cursor: 'pointer' },
            };
          }}
          scroll={{
            x: 'max-content',
          }}
          rowHoverable={true}
        />
      </Box>
    </Box>
  );
};

export default CompanyList;
